import type { LicenseSummaryDto } from '@dbrain/shared';
import { Icons } from '@dbrain/ui';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useMe } from '../../api/hooks.js';
import { isDetail, isSuperAdmin, useLicense } from '../../api/license.js';
import { addDays, licenseDayAt, licenseDayFrom, licenseDayTo, licenseRange } from '@dbrain/app-core/license-days';

/** the gap kept between the popover and the window's edges */
const EDGE = 8;

type Step = 'reminder' | 'grace' | 'readOnly' | 'trial' | 'license';
interface StepView {
  step: Step;
  when: string;
  state: 'done' | 'now' | 'next';
}

/** The steps with their days and "Şimdi" under the one this is; the trial's in blue (amber in its last 7 days). */
function Steps({ steps, tone }: { steps: StepView[]; tone: 'info' | 'warning' }) {
  const { t } = useTranslation();
  return (
    <ol className={`license-steps license-steps--${steps.length}`} data-testid="license-steps">
      {steps.map(({ step, when, state }) => (
        <li key={step} className={`license-steps__item license-steps__item--${state} license-steps__item--${step} license-steps__item--${tone}`} data-state={state}>
          <span className="license-steps__mark" aria-hidden="true">
            {state === 'done' ? <Icons.check size={12} /> : null}
          </span>
          <span className="license-steps__name">{t(`license.popover.steps.${step}`)}</span>
          <span className="license-steps__when">{when}</span>
          {state === 'now' ? (
            <span className="license-steps__now" data-testid="license-steps-now">
              {t('license.popover.now')}
            </span>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

/** A license's three steps (Hatırlatma, Ek süre, Salt okunur), this one "Şimdi" (13f). */
function licenseSteps(license: LicenseSummaryDto, now: 'reminder' | 'grace' | 'readOnly', locale: string, from: (day: string) => string): StepView[] {
  const { endsOn, reminderFrom, graceFrom, readOnlyFrom } = license;
  if (!endsOn || !reminderFrom || !graceFrom || !readOnlyFrom) return [];
  const order = ['reminder', 'grace', 'readOnly'] as const;
  const when: Record<(typeof order)[number], string> = {
    reminder: licenseRange(reminderFrom, endsOn, locale, false),
    grace: licenseRange(graceFrom, addDays(readOnlyFrom, -1), locale, false),
    readOnly: from(readOnlyFrom),
  };
  const at = order.indexOf(now);
  return order.map((step, index) => ({ step, when: when[step], state: index < at ? 'done' : index === at ? 'now' : 'next' }));
}

/**
 * The free trial's steps (LisansDenemeRozet, D-133): the trial, then read-only from the day after it, or the
 * license that takes over; with a gap before a queued license both.
 */
function trialSteps(license: LicenseSummaryDto, nextFrom: string | null, locale: string, from: (day: string) => string): StepView[] {
  const { trial, endsOn, readOnlyFrom } = license;
  if (!trial || !endsOn) return [];
  const over = license.status === 'read_only';
  const steps: StepView[] = [{ step: 'trial', when: licenseRange(trial.from, endsOn, locale, false), state: over ? 'done' : 'now' }];
  if (readOnlyFrom) steps.push({ step: 'readOnly', when: from(readOnlyFrom), state: over ? 'now' : 'next' });
  const takesOver = nextFrom ?? (readOnlyFrom ? null : addDays(endsOn, 1));
  if (takesOver) steps.push({ step: 'license', when: from(takesOver), state: 'next' });
  return steps;
}

/**
 * The license chip (LisansRozet, D-126) next to the model button in a conversation's bar and in the top bar
 * elsewhere: amber "Lisans: N gün" in the reminder (super admins only) and the grace (everyone), red
 * "Salt okunur" for everyone after it. In the free trial (LisansDenemeRozet, D-133) blue "Deneme: N gün" for
 * super admins only, amber in its last 7 days; read-only after it as after a license. Nothing while the license
 * is active, and no banners. A click opens the steps with "Şimdi" under this one; super admins get the way to
 * the Lisans page.
 */
export function LicenseChip() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const me = useMe().data;
  const license = useLicense().data;
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const titleId = useId();
  const admin = isSuperAdmin(me);
  // opened: focus in the popover; a click elsewhere closes it
  useEffect(() => {
    if (!open) return;
    panel.current?.focus();
    const onPointer = (event: PointerEvent) => {
      if (event.target instanceof Node && !wrapper.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);
  // opened: kept inside the window (the bar's controls wrap to the left on a narrow one)
  useLayoutEffect(() => {
    const node = panel.current;
    if (!open || !node) return;
    const place = () => {
      node.style.removeProperty('--license-shift');
      const rect = node.getBoundingClientRect();
      if (rect.left < EDGE) node.style.setProperty('--license-shift', `${Math.round(EDGE - rect.left)}px`);
      else if (rect.right > window.innerWidth - EDGE) node.style.setProperty('--license-shift', `${Math.round(window.innerWidth - EDGE - rect.right)}px`);
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  if (!license?.enabled) return null;
  const { status } = license;
  if (status === 'active' || ((status === 'reminder' || status === 'trial') && !admin)) return null;
  const locale = i18n.language;
  const from = (day: string) => t('license.popover.from', { day: licenseDayFrom(day, locale) });
  const days = license.daysLeft ?? 0;
  const trial = license.basis === 'trial';
  // the trial's last 7 days turn it amber, when read-only follows
  const late = status === 'trial' && license.reminderFrom !== null && days <= 7;
  const tone = status === 'read_only' ? 'danger' : status === 'trial' && !late ? 'info' : 'warning';
  const label = status === 'read_only' ? t('license.chip.readOnly') : status === 'trial' ? t('license.chip.trial', { count: days }) : t('license.chip.days', { count: days });
  const next = isDetail(license) ? license.next : null;
  const steps = trial
    ? trialSteps(license, next?.validFrom ?? null, locale, from)
    : licenseSteps(license, status === 'read_only' ? 'readOnly' : status === 'trial' ? 'reminder' : status, locale, from);
  const title = t(status === 'trial' ? 'license.popover.titles.trial' : `license.popover.titles.${status === 'read_only' ? 'readOnly' : status}`);
  const body =
    status === 'trial' && license.endsOn
      ? t('license.popover.trialBody', { day: licenseDayTo(license.endsOn, locale) })
      : trial && license.trial
        ? t('license.popover.trialEndedBody', { day: licenseDayAt(license.trial.until, locale, true) })
        : status === 'reminder' && license.endsOn
          ? t('license.popover.reminderBody', { day: licenseDayAt(license.endsOn, locale, true) })
          : status === 'grace' && license.endsOn
            ? t('license.popover.graceBody', { day: licenseDayAt(license.endsOn, locale), count: days })
            : !license.endsOn
              ? t('license.popover.noLicenseBody')
              : t('license.popover.readOnlyBody', { day: licenseDayAt(license.endsOn, locale, true) });
  const readOnlyFrom = license.readOnlyFrom ? licenseDayFrom(license.readOnlyFrom, locale) : '';
  const note =
    status === 'trial'
      ? next
        ? t('license.popover.trialNoteQueued', { day: licenseDayAt(next.validFrom, locale) })
        : t('license.popover.trialNote')
      : status === 'read_only'
        ? t(admin ? 'license.popover.readOnlyNoteAdmin' : 'license.popover.readOnlyNote')
        : status === 'grace'
          ? t(admin ? 'license.popover.graceNoteAdmin' : 'license.popover.graceNote', { day: readOnlyFrom })
          : t('license.popover.reminderNote');
  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) button.current?.focus();
  };
  return (
    <div ref={wrapper} className="license-chip" data-testid="license-chip" data-status={status} data-tone={tone}>
      <button
        ref={button}
        type="button"
        className={`license-chip__button license-chip__button--${tone}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={label}
        title={t('license.chip.hint')}
        onClick={() => setOpen((value) => !value)}
        data-testid="license-chip-button"
      >
        {status === 'read_only' ? <Icons.lock size={15} /> : status === 'trial' ? <Icons.gift size={15} /> : <Icons.time size={15} />}
        <span>{label}</span>
      </button>
      {open ? (
        <div
          ref={panel}
          id={panelId}
          className="license-popover"
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          tabIndex={-1}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation();
              close(true);
            }
          }}
          data-testid="license-popover"
        >
          <h2 id={titleId} className="license-popover__title">
            {title}
          </h2>
          <p className="license-popover__body">{body}</p>
          {steps.length > 0 ? <Steps steps={steps} tone={status === 'trial' && !late ? 'info' : 'warning'} /> : null}
          <p className="license-popover__note">{note}</p>
          {admin ? (
            <div className="license-popover__actions">
              <button
                type="button"
                className="ui-button ui-button--primary ui-button--sm"
                onClick={() => {
                  close(false);
                  void navigate('/admin/license');
                }}
                data-testid="license-popover-open"
              >
                {t('license.openPage')}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
